import { View } from "extensions/marionette";
import { CollectionView } from "backbone.marionette";
import { Backbone } from "extensions/backbone";
import { captureException } from "extensions/sentry";
import startsWith from "lodash-es/startsWith";

import { destroyPopovers } from "common/utils";
import { VirtualizedListView } from "common/components/virtualized/virtualizedListView";

import dropdownTemplate from "./dropdown.jst";

const DropdownItemView = View.extend({
    attributes: {
        role: "presentation"
    },
    className() {
        if (this.model.get("selected")) {
            return "selected";
        }
        
        return "";
    },
    
    tagName: "li",
    
    events: {
        "click a": function(e) {
            e.preventDefault();
            this.triggerMethod("clicked:item", this, e);
        }
    },
    
    template: ({ name }) => `<a role='menuitem' tabIndex='-1' href='#'>${name}</a>`,
    
    hide() {
        return this.$el.addClass("hidden");
    },
    
    setFocus() {
        this.$el.find("a").trigger("focus");
    }
});

const DropdownParams = {
    tagName: "ul",
    className: "dropdown-menu-list",
    childView: DropdownItemView,
    childViewTriggers: {
        "clicked:item": "clicked:item"
    }
};
const DropdownVirtualizedCollectionView = VirtualizedListView.extend({
    ...DropdownParams,
    className: "dropdown-menu-list virtualized"
});

const DropdownCollectionView = CollectionView.extend(DropdownParams);

export const Dropdown = View.extend({
    className() {
        const base = ["pz-dropdown"];
        if (this.getOption("style")) {
            base.push(this.getOption("style"));
        }
        
        return base.join(" ");
    },
    template: dropdownTemplate,
    ui: {
        filterInput: ".dropdown-menu-filter input",
        currentValue: ".dropdown-toggle .value"
    },
    events: {
        "shown.bs.dropdown": "shown",
        "hidden.bs.dropdown": "hidden",
        "keydown @ui.filterInput": "handleFilterKeydown",
        "keyup @ui.filterInput": "handleFilterKeyup",
        "keydown li": "handleChildKeydown",
        "click .clear-value": "clearFilterValue",
        "click .clear-dropdown": "clearValue"
    },
    regions: {
        dropDown: {
            el: ".dropdown-menu-list",
            replaceElement: true
        }
    },
    childViewEvents: {
        "clicked:item": "setNewValueFromChildview"
    },
    triggers: {
        "click .header": "header:click"
    },
    showDropDown() {
        this.showChildView("dropDown", this.dd);
    },
    
    property: "value",
    collectionIdAttribute: "value",
    virtualized: true,
    showFilterInput: false,
    direction: {
        vertical: "down",
        horizontal: "left"
    },
    placeholder: "",
    selected: void 0,
    
    initialize() {
        this.state = {};
        
        this.setCollection();
        
        let DDConstructor;
        if (this.getOption("virtualized")) {
            DDConstructor = DropdownVirtualizedCollectionView;
        } else {
            DDConstructor = DropdownCollectionView;
        }
        this.dd = new DDConstructor({
            collection: this.collection
        });
        
        if (this.model) {
            this.listenTo(this.model, `change:${this.getOption("property")}`, this.setActiveItem);
        }
    },
    
    setCollection(collection = this.collection) {
        // Allow regular arrays instead of collection
        if (!(collection instanceof Backbone.Collection) && Array.isArray(collection)) {
            this.collection = new Backbone.Collection(collection);
            if (typeof this.getOption("comparator") === "function") {
                this.collection.comparator = this.getOption("comparator");
                this.collection.sort();
            }
            
            if (this.getOption("selected")) {
                const selected = this.collection.find((m) => m.get(this.getOption("collectionIdAttribute")) === this.getOption("selected"));
                if (selected) {
                    selected.set({ selected: true });
                }
            }
        }
    },
    
    setActiveItem(model, value) {
        if (this.isDestroyed()) return;
        
        const activeModel = this.getActiveNameByValue(value);
        this.value = value;
        this.toggleClearIcon(value);
        
        // Just set value if there is no model with such value in the collection
        // this is necessary use case for currency dropdown
        this.ui.currentValue.text(activeModel ? activeModel.get("name") : value);
    },
    
    clearValue(e) {
        e.stopPropagation();
        this.value = "";
        this.setActiveItem({}, "");
        this.triggerMethod("change:value", "", {});
        this.toggleClearIcon();
    },
    
    toggleClearIcon(value) {
        this.$el.addClass("has-value", !!value);
        if (!this.getOption("showClearIcon")) return;
        this.$el.find(".clear-dropdown").toggleClass("hidden", !value);
    },
    
    getValue() {
        return this.value;
    },
    
    setNewValueFromChildview(view) {
        const value = view.model.get(this.getOption("collectionIdAttribute"));
        if (this.model) {
            this.model.set(this.getOption("property"), value);
        } else if (this.value !== value) {
            this.triggerMethod("change:value", value, view.model);
            this.setActiveItem(null, value);
        }
    },
    
    getActiveNameByValue(value) {
        const selectedModel = this.collection.findWhere({
            [this.getOption("collectionIdAttribute")]: value
        }) || this.collection.findWhere({ selected: true }) || this.collection.first();
        
        if (!selectedModel) {
            // Disable dropdown as there are no alternatives to the current item
            this.disableDropdown();
            return false;
        }
        
        return selectedModel;
    },
    disableDropdown() {
        // Code to disabled dropdown. Add later if we actually need it.
    },
    
    warnDeprecations() {
        ["label", "footer", "header"].forEach((o) => {
            if (this.getOption(o)) {
                captureException(new Error(`'${o}' option is deprecated from dropdown`));
            }
        });
    },
    
    templateContext() {
        this.warnDeprecations();
        const direction = {
            ...this.direction,
            ...this.getOption("direction")
        };
        
        return {
            label: this.getOption("label"),
            filter: this.hasFilter(),
            placeholder: this.getOption("placeholder"),
            hasClearButton: this.getOption("showClearIcon"),
            verticalClassName: `drop${direction.vertical}`,
            horizontalClassName: `dropdown-menu pull-${direction.horizontal} filter-${this.hasFilter()}`
        };
    },
    
    hasFilter() {
        return this.getOption("showFilterInput");
    },
    
    onRender() {
        this.showDropDown();
        
        // If there is no placeholder then find selected in model or collection
        if (this.getOption("selected") && !this.state.initialSelectedRendered) {
            this.state.initialSelectedRendered = true;
            this.setActiveItem(null, this.getOption("selected"));
        } else if (!this.getOption("placeholder") && !this.getOption("selected")) {
            const model = this.model || this.collection.findWhere({ selected: true }) || this.collection.first();
            const active = model ? model.get(this.getOption("property")) : void 0;
            
            this.setActiveItem(null, active);
        }
    },
    /**
     * Hide Dropdown
     * @returns {jQuery Object}
     */
    hide() {
        this.hidden = true;
        return this.$el.hide();
    },
    
    show() {
        if (this.hidden) {
            this.hidden = false;
            this.$el.show();
        }
    },
    
    shown() {
        if (this.hasFilter()) {
            this.ui.filterInput.focus();
            
            // If filtering is enabled, we need to make sure that dropdown width doesn't change when typing
            const $list = this.$(".dropdown-menu-list");
            $list.width($list.width());
        }
    },
    
    hidden() {
        if (this.hasFilter()) {
            this.ui.filterInput.blur();
            this.clearFilterValue();
        }
        
        destroyPopovers();
        this.triggerMethod("hidden");
    },
    
    handleChildKeydown(event) {
        if (event.which === 38 && this.$(event.currentTarget).is(":first-child")) { // 40 is arrow-up
            // Focus on the input
            this.ui.filterInput.focus();
            event.stopPropagation();
            event.preventDefault();
        }
    },
    
    getDropdownChildren() {
        return this.dd.getChildren();
    },
    
    handleFilterKeydown(event) {
        if (event.which === 40 && this.getDropdownChildren().length) { // 40 is arrow-down
            // Find first child that is not disabled and is visible
            this.getDropdownChildren().find((child) => child.$el.is(":not(.disabled):visible")).setFocus();
            event.stopPropagation();
            event.preventDefault();
        }
    },
    
    handleFilterKeyup(event) {
        if (!/(38|40|27|32)/.test(event.which)) {
            const value = this.ui.filterInput.val();
            
            if (value) {
                this.dd.setFilter((model) => model.get("name") && startsWith(model.get("name").toLowerCase(), value.toLowerCase()));
            } else {
                this.dd.removeFilter();
            }
            this.$el.toggleClass("has-input-value", !!value);
        }
    },
    
    clearFilterValue(event) {
        this.ui.filterInput.val("");
        this.dd.removeFilter();
        
        if (event) {
            // Do not propagate event up to prevent menu from being closed
            event.stopPropagation();
            event.preventDefault();
        }
    },
    
    removeEl() {
        if (!this.getOption("keepElOnDestroy")) {
            View.prototype.removeEl.apply(this, arguments);
        }
    },
    
    toggle() {
        this.$(".dropdown-toggle").dropdown("toggle");
    }
});
